"use client";

import { lazy } from "react";
import type { ToolUIMeta, ToolUIRegistry } from "../../tool-renderer";

export const procoreToolRegistry = {
  procoreListCompanies: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ ProcoreListCompaniesToolView }) => ({
        default: ProcoreListCompaniesToolView,
      }))
    ),
  },
  procoreListProjects: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ ProcoreListProjectsToolView }) => ({
        default: ProcoreListProjectsToolView,
      }))
    ),
  },
  procoreListResource: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ ProcoreListResourceToolView }) => ({
        default: ProcoreListResourceToolView,
      }))
    ),
  },
  procoreListResources: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ ProcoreListResourcesToolView }) => ({
        default: ProcoreListResourcesToolView,
      }))
    ),
  },
  procoreGetResource: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ ProcoreGetResourceToolView }) => ({
        default: ProcoreGetResourceToolView,
      }))
    ),
  },
  procoreGetRfi: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ ProcoreGetRfiToolView }) => ({
        default: ProcoreGetRfiToolView,
      }))
    ),
  },
  procoreGetSubmittal: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ ProcoreGetSubmittalToolView }) => ({
        default: ProcoreGetSubmittalToolView,
      }))
    ),
  },
  procoreCreateRfi: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./create-rfi").then(({ ProcoreCreateRfiToolView }) => ({
        default: ProcoreCreateRfiToolView,
      }))
    ),
  },
  procoreUpdateRfi: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./update-rfi").then(({ ProcoreUpdateRfiToolView }) => ({
        default: ProcoreUpdateRfiToolView,
      }))
    ),
  },
  procoreCreateSubmittal: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./create-submittal").then(
        ({ ProcoreCreateSubmittalToolView }) => ({
          default: ProcoreCreateSubmittalToolView,
        })
      )
    ),
  },
  procoreUpdateSubmittal: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./update-submittal").then(
        ({ ProcoreUpdateSubmittalToolView }) => ({
          default: ProcoreUpdateSubmittalToolView,
        })
      )
    ),
  },
  procoreGetScheduleStatus: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ ProcoreGetScheduleStatusToolView }) => ({
        default: ProcoreGetScheduleStatusToolView,
      }))
    ),
  },
  procoreCreateObservation: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./views").then(({ ProcoreCreateObservationToolView }) => ({
        default: ProcoreCreateObservationToolView,
      }))
    ),
  },
  procoreUpdateObservation: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./views").then(({ ProcoreUpdateObservationToolView }) => ({
        default: ProcoreUpdateObservationToolView,
      }))
    ),
  },
  procoreCreatePunchItem: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./views").then(({ ProcoreCreatePunchItemToolView }) => ({
        default: ProcoreCreatePunchItemToolView,
      }))
    ),
  },
  procoreUpdatePunchItem: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./views").then(({ ProcoreUpdatePunchItemToolView }) => ({
        default: ProcoreUpdatePunchItemToolView,
      }))
    ),
  },
  procoreUpdateChangeEvent: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./views").then(({ ProcoreUpdateChangeEventToolView }) => ({
        default: ProcoreUpdateChangeEventToolView,
      }))
    ),
  },
  procoreRequestScheduleChange: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./views").then(({ ProcoreRequestScheduleChangeToolView }) => ({
        default: ProcoreRequestScheduleChangeToolView,
      }))
    ),
  },
  procoreAddDailyLogNote: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./views").then(({ ProcoreAddDailyLogNoteToolView }) => ({
        default: ProcoreAddDailyLogNoteToolView,
      }))
    ),
  },
  procoreUploadDocument: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./views").then(({ ProcoreUploadDocumentToolView }) => ({
        default: ProcoreUploadDocumentToolView,
      }))
    ),
  },
  procoreUploadDrawing: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./views").then(({ ProcoreUploadDrawingToolView }) => ({
        default: ProcoreUploadDrawingToolView,
      }))
    ),
  },
} satisfies ToolUIRegistry;

/**
 * Namespaced variant of `procoreToolRegistry` keyed by the progressive-disclosure
 * inner-tool name `procore_${toolName}`.
 *
 * Procore is a progressive-disclosure capability (ADR-057), so its tool calls are
 * dispatched through the `callCapabilityTool` proxy and every durable +
 * live-stream tool part is rewritten to the namespaced inner identity
 * `${capabilityId}_${toolName}` (e.g. `procore_procoreListResource`). The
 * bare-name entries above stay in the static tier (still required by
 * `AllToolNames` and used by legacy persisted chats); these namespaced entries
 * reuse the same lazy views so the rich Procore cards render on the PD path
 * instead of falling through to the generic JSON fallback. Wired into the
 * built-in registry in `packages/sdk/react/src/tools/built-in-registry.ts`.
 */
export const procoreProgressiveDisclosureToolRegistry: Record<
  string,
  ToolUIMeta
> = Object.fromEntries(
  Object.entries(procoreToolRegistry).map(([name, meta]) => [
    `procore_${name}`,
    meta,
  ])
);
